import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface Order {
  id: string;
  product_id: string;
  product_name: string;
  product_category: string;
  product_price: number;
  product_original_price: number;
  product_artistid?: string; // Add artist ID field
  product_main_image?: string; // Add product main image field
  selected_size_name?: string; // Add selected size name field
  customer_first_name: string;
  customer_last_name: string;
  customer_email: string;
  customer_phone: string;
  delivery_address: string;
  delivery_city: string;
  delivery_state: string;
  delivery_pincode: string;
  custom_photo_url?: string;
  custom_text?: string;
  special_remarks?: string;
  payment_method: string;
  payment_status: string;
  transaction_id?: string;
  payment_amount: number;
  order_status: string;
  order_date: string;
  estimated_delivery_date?: string;
  actual_delivery_date?: string;
  expected_production_date?: string;
  tracking_number?: string;
  shipping_carrier?: string;
  created_at: string;
  updated_at: string;
  notes?: string;
  is_custom_order: boolean;
  priority_level: string;
  user_id?: string;
}

export interface CreateOrderData {
  product_id: string;
  product_name: string;
  product_category: string;
  product_price: number;
  product_original_price?: number;
  product_artistid?: string; // Add artist ID field
  customer_first_name: string;
  customer_last_name: string;
  customer_email: string;
  customer_phone: string;
  delivery_address: string;
  delivery_city: string;
  delivery_state: string;
  delivery_pincode: string;
  custom_photo_url?: string;
  custom_text?: string;
  special_remarks?: string;
  payment_method: string;
  payment_status: string;
  payment_amount: number;
  order_status: string;
  estimated_delivery_date?: string;
  expected_production_date?: string;
  is_custom_order?: boolean;
  priority_level?: string;
  notes?: string;
}

export function useOrders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("🔍 Fetching orders from Supabase...");
      console.log("🔧 Supabase URL:", supabase.supabaseUrl);
      console.log("🔧 Supabase Key length:", supabase.supabaseKey?.length || 0);

      const { data, error: fetchError } = await supabase
        .from("orders")
        .select(`
          *,
          products!inner(
            artistid
          )
        `)
        .order("created_at", { ascending: false });

      console.log("🔍 Supabase response:", { data, error: fetchError });

      if (fetchError) {
        console.error("❌ Supabase fetch error:", fetchError);
        throw fetchError;
      }

      console.log("✅ Orders fetched successfully:", data);
      
      // Process the joined data to extract artist ID
      const processedOrders = (data || []).map(order => ({
        ...order,
        product_artistid: order.products?.artistid || null
      }));
      
      setOrders(processedOrders);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch orders";
      console.error("❌ Error in fetchOrders:", err);
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const createOrder = useCallback(async (orderData: CreateOrderData) => {
    try {
      setLoading(true);
      setError(null);

      const newOrder = {
        ...orderData,
        order_date: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_custom_order: orderData.is_custom_order || false,
        priority_level: orderData.priority_level || "normal",
      };

      const { data, error: createError } = await supabase
        .from("orders")
        .insert([newOrder])
        .select()
        .single();

      if (createError) {
        throw createError;
      }

      setOrders(prev => [data, ...prev]);
      
      toast({
        title: "Success",
        description: "Order created successfully",
      });

      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to create order";
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const updateOrder = useCallback(async (orderId: string, orderData: Partial<Order>) => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔧 Updating order:', orderId, 'with data:', orderData);

      // Filter out undefined/null values to prevent database errors
      const cleanOrderData = Object.fromEntries(
        Object.entries(orderData).filter(([_, value]) => value !== undefined && value !== null)
      );

      const updateData = {
        ...cleanOrderData,
        updated_at: new Date().toISOString(),
      };

      console.log('🧹 Cleaned update data:', updateData);

      const { data, error: updateError } = await supabase
        .from("orders")
        .update(updateData)
        .eq("id", orderId)
        .select()
        .single();

      if (updateError) {
        console.error('❌ Supabase update error:', updateError);
        throw updateError;
      }

      console.log('✅ Order updated successfully:', data);

      setOrders(prev => 
        prev.map(order => 
          order.id === orderId ? { ...order, ...data } : order
        )
      );

      toast({
        title: "Success",
        description: "Order updated successfully",
      });

      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to update order";
      console.error("❌ Error in updateOrder:", err);
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const deleteOrder = useCallback(async (orderId: string) => {
    try {
      setLoading(true);
      setError(null);

      const { error: deleteError } = await supabase
        .from("orders")
        .delete()
        .eq("id", orderId);

      if (deleteError) {
        throw deleteError;
      }

      setOrders(prev => prev.filter(order => order.id !== orderId));

      toast({
        title: "Success",
        description: "Order deleted successfully",
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to delete order";
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const getOrderById = useCallback(async (orderId: string) => {
    try {
      const { data, error: fetchError } = await supabase
        .from("orders")
        .select("*")
        .eq("id", orderId)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch order";
      setError(errorMessage);
      throw err;
    }
  }, []);

  const updateOrderStatus = useCallback(async (orderId: string, status: string) => {
    return updateOrder(orderId, { order_status: status });
  }, [updateOrder]);

  const updatePaymentStatus = useCallback(async (orderId: string, status: string) => {
    return updateOrder(orderId, { payment_status: status });
  }, [updateOrder]);

  const searchOrders = useCallback(async (searchTerm: string) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: searchError } = await supabase
        .from("orders")
        .select("*")
        .or(`customer_first_name.ilike.%${searchTerm}%,customer_last_name.ilike.%${searchTerm}%,customer_email.ilike.%${searchTerm}%,product_name.ilike.%${searchTerm}%`)
        .order("created_at", { ascending: false });

      if (searchError) {
        throw searchError;
      }

      setOrders(data || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to search orders";
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const filterOrdersByStatus = useCallback(async (status: string) => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from("orders")
        .select("*")
        .order("created_at", { ascending: false });

      if (status !== "all") {
        query = query.eq("order_status", status);
      }

      const { data, error: filterError } = await query;

      if (filterError) {
        throw filterError;
      }

      setOrders(data || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to filter orders";
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const testSupabaseConnection = useCallback(async () => {
    try {
      console.log('🧪 Testing Supabase connection...');
      
      // Test basic select
      const { data: testData, error: testError } = await supabase
        .from('orders')
        .select('id, order_status')
        .limit(1);
      
      if (testError) {
        console.error('❌ Test select failed:', testError);
        return false;
      }
      
      console.log('✅ Test select successful:', testData);
      
      // Test basic update
      if (testData && testData.length > 0) {
        const testOrderId = testData[0].id;
        const { data: updateData, error: updateError } = await supabase
          .from('orders')
          .update({ notes: 'Test update from client' })
          .eq('id', testOrderId)
          .select('id, notes')
          .single();
        
        if (updateError) {
          console.error('❌ Test update failed:', updateError);
          return false;
        }
        
        console.log('✅ Test update successful:', updateData);
      }
      
      return true;
    } catch (error) {
      console.error('❌ Test connection failed:', error);
      return false;
    }
  }, []);

  return {
    orders,
    loading,
    error,
    fetchOrders,
    createOrder,
    updateOrder,
    deleteOrder,
    getOrderById,
    updateOrderStatus,
    updatePaymentStatus,
    searchOrders,
    filterOrdersByStatus,
    testSupabaseConnection,
  };
}
